import { Button, Component, Enum, Node, _decorator } from 'cc';
import { EDITOR } from 'cc/env';
import NodeName from '../../Data/Config/SceneNodeName';
import ItemGenerator from '../../../../common/scripts/Toolkit/Components/Misc/ItemGenerator';
import MultiThemeSource from '../../../../common/scripts/Toolkit/Components/MultiTheme/MultiThemeSource';
import MultiThemeSprite from '../../../../common/scripts/Toolkit/Components/MultiTheme/MultiThemeSprite';
import MultiThemeLabel from '../../../../common/scripts/Toolkit/Components/MultiTheme/MultiThemeLabel';
import Page from '../../../../common/scripts/Toolkit/Components/Page/Page';
import { FeatureActivityEventName } from '../../Data/Config/FeatureActivityConfig';
import { GameSettingName } from '../../../../tableEngine/Script/Game/Data/Model/SettingModel';
import Lobby from '../../../System/Lobby';

enum TutorialPage {
    Lobby_Intro,
    Lobby_EnterGame,
    Lobby_MachineStats,
    Lobby_MachinePlayerCount,
    Lobby_MachineInfo,
    Lobby_PlayTypeSelection,
    Lobby_QuickJoin,
    Lobby_FreePlay,
    Lobby_LobbyJackpot,
    Lobby_BackToOuterLobby,
    Lobby_Tutorial,

    // play specific
    CrowdPlay_Intro,
    MultiplePlay_Intro,
    RoundPlay_Intro,
    SinglePlay_Intro,
    SinglePlay_BetUI,
    MultiplePlay_BetUI,
    RoundPlay_BetUI,
    CrowdPlay_BetUI,
    CrowdPlay_CreateOrJoin,

    // play shared
    Play_GameMessageBar,
    Play_CameraSwitch,
    Play_BalanceAndMachineInfo,
    Play_Roadmap,
    Play_FavoriteAndMenu,
    Play_BetPercentageBar,
    Play_PlayerCount,
    Play_ChatRoom,

    RoundPlay_GameMessageBar,
    RoundPlay_CameraSwitch,

    // new added
    Play_JackpotPercentageBar,

    // simple multiple play
    SimpleMultiplePlay_AutoPlayButton,
    SimpleMultiplePlay_BetUI,
    SimpleMultiplePlay_BetPercentageBar,
    SimpleMultiplePlay_JackpotPercentageBar,
    SimpleMultiplePlay_PlayerCount,
    SimpleMultiplePlay_MessageMarquee,
    SimpleMultiplePlay_CameraSwitch,
    SimpleMultiplePlay_ChatRoom,
}

export enum TutorialEventType {
    DISPLAY = 'DISPLAY',
}

export enum TutorialType {
    OuterLobby,
    MacauLobby,
    CrowdPlay,
    MultiplePlay,
    SimpleMultiplePlay,
    RoundPlay,
    SinglePlay,
}

type TutorialSettingName =
    | GameSettingName.OuterLobbyTutorialShow
    | GameSettingName.OuterLobbyTutorialComplete
    | GameSettingName.LobbyTutorialShow
    | GameSettingName.LobbyTutorialComplete
    | GameSettingName.CrowdPlayTutorialShow
    | GameSettingName.CrowdPlayTutorialComplete
    | GameSettingName.MultiplePlayTutorialShow
    | GameSettingName.MultiplePlayTutorialComplete
    | GameSettingName.RoundPlayTutorialShow
    | GameSettingName.RoundPlayTutorialComplete
    | GameSettingName.SinglePlayTutorialShow
    | GameSettingName.SinglePlayTutorialComplete;

const { ccclass, property, requireComponent } = _decorator;
const TUTORIAL_PLAYLIST_MAP: { [key in TutorialType]: TutorialPage[] } = {
    [TutorialType.OuterLobby]: [],
    [TutorialType.MacauLobby]: [
        TutorialPage.Lobby_Intro,
        TutorialPage.Lobby_EnterGame,
        TutorialPage.Lobby_MachineStats,
        TutorialPage.Lobby_MachinePlayerCount,
        TutorialPage.Lobby_MachineInfo,
        TutorialPage.Lobby_PlayTypeSelection,
        TutorialPage.Lobby_QuickJoin,
        TutorialPage.Lobby_FreePlay,
        TutorialPage.Lobby_LobbyJackpot,
        TutorialPage.Lobby_BackToOuterLobby,
        TutorialPage.Lobby_Tutorial,
    ],
    [TutorialType.CrowdPlay]: [
        TutorialPage.CrowdPlay_Intro,
        TutorialPage.CrowdPlay_BetUI,
        TutorialPage.CrowdPlay_CreateOrJoin,
        TutorialPage.Play_GameMessageBar,
        TutorialPage.Play_BetPercentageBar,
        TutorialPage.Play_CameraSwitch,
        TutorialPage.Play_PlayerCount,
        TutorialPage.Play_ChatRoom,
        TutorialPage.Play_BalanceAndMachineInfo,
        TutorialPage.Play_Roadmap,
        TutorialPage.Play_FavoriteAndMenu,
    ],
    [TutorialType.MultiplePlay]: [
        TutorialPage.Play_CameraSwitch,
        TutorialPage.MultiplePlay_Intro,
        TutorialPage.MultiplePlay_BetUI,
        TutorialPage.Play_GameMessageBar,
        TutorialPage.Play_BetPercentageBar,
        TutorialPage.Play_JackpotPercentageBar,
        TutorialPage.Play_PlayerCount,
        TutorialPage.Play_ChatRoom,
        TutorialPage.Play_BalanceAndMachineInfo,
        TutorialPage.Play_Roadmap,
        TutorialPage.Play_FavoriteAndMenu,
    ],
    [TutorialType.SimpleMultiplePlay]: [
        TutorialPage.SimpleMultiplePlay_CameraSwitch,
        TutorialPage.MultiplePlay_Intro,
        TutorialPage.SimpleMultiplePlay_AutoPlayButton,
        TutorialPage.SimpleMultiplePlay_BetUI,
        TutorialPage.SimpleMultiplePlay_BetPercentageBar,
        TutorialPage.SimpleMultiplePlay_JackpotPercentageBar,
        TutorialPage.SimpleMultiplePlay_ChatRoom,
        TutorialPage.SimpleMultiplePlay_PlayerCount,
        TutorialPage.SimpleMultiplePlay_MessageMarquee,
        TutorialPage.Play_Roadmap,
        TutorialPage.Play_FavoriteAndMenu,
    ],
    [TutorialType.RoundPlay]: [
        TutorialPage.RoundPlay_Intro,
        TutorialPage.RoundPlay_BetUI,
        TutorialPage.RoundPlay_GameMessageBar,
        TutorialPage.RoundPlay_CameraSwitch,
        TutorialPage.Play_PlayerCount,
        TutorialPage.Play_ChatRoom,
        TutorialPage.Play_BalanceAndMachineInfo,
        TutorialPage.Play_Roadmap,
        TutorialPage.Play_FavoriteAndMenu,
    ],
    [TutorialType.SinglePlay]: [
        TutorialPage.SinglePlay_Intro,
        TutorialPage.SinglePlay_BetUI,
        TutorialPage.Play_GameMessageBar,
        TutorialPage.Play_CameraSwitch,
        TutorialPage.Play_BalanceAndMachineInfo,
        TutorialPage.Play_Roadmap,
        TutorialPage.Play_FavoriteAndMenu,
    ],
};
const TUTORIAL_SETTING_MAP: { [key in TutorialType]: { show: TutorialSettingName; complete: TutorialSettingName } } = {
    [TutorialType.OuterLobby]: { show: GameSettingName.OuterLobbyTutorialShow, complete: GameSettingName.OuterLobbyTutorialComplete },
    [TutorialType.MacauLobby]: { show: GameSettingName.LobbyTutorialShow, complete: GameSettingName.LobbyTutorialComplete },
    [TutorialType.CrowdPlay]: { show: GameSettingName.CrowdPlayTutorialShow, complete: GameSettingName.CrowdPlayTutorialComplete },
    [TutorialType.MultiplePlay]: { show: GameSettingName.MultiplePlayTutorialShow, complete: GameSettingName.MultiplePlayTutorialComplete },
    [TutorialType.SimpleMultiplePlay]: { show: GameSettingName.MultiplePlayTutorialShow, complete: GameSettingName.MultiplePlayTutorialComplete },
    [TutorialType.RoundPlay]: { show: GameSettingName.RoundPlayTutorialShow, complete: GameSettingName.RoundPlayTutorialComplete },
    [TutorialType.SinglePlay]: { show: GameSettingName.SinglePlayTutorialShow, complete: GameSettingName.SinglePlayTutorialComplete },
};
const TUTORIAL_NAME_MAP: { [key in TutorialType]: string } = {
    [TutorialType.OuterLobby]: 'Outer Lobby',
    [TutorialType.MacauLobby]: 'Lobby',
    [TutorialType.CrowdPlay]: 'Crowd Play',
    [TutorialType.MultiplePlay]: 'Multiple Play',
    [TutorialType.SimpleMultiplePlay]: 'Simple Multiple Play',
    [TutorialType.RoundPlay]: 'Round Play',
    [TutorialType.SinglePlay]: 'Single Play',
};
const DISABLE_AUTOPLAY_TUTORIAL = true;

@ccclass('TutorialPlayer')
@requireComponent([MultiThemeSource, Page])
export default class TutorialPlayer extends Component {
    public static readonly EVENT_TYPE = TutorialEventType;

    @property(Node)
    private readonly focusOnMask: Node = null;

    @property(Node)
    private readonly infoBoard: Node = null;

    @property(ItemGenerator)
    private readonly pageIndicator: ItemGenerator = null;

    @property(Button)
    private readonly nextButton: Button = null;

    @property(Button)
    private readonly skipButton: Button = null;

    @property({ type: Enum(TutorialPage) })
    private _displayPage: TutorialPage = TutorialPage.Lobby_Intro;

    private _multiThemeSource: MultiThemeSource = null;
    private _page: Page = null;
    private _nextButtonText: MultiThemeLabel = null;
    private _itemMultiThemeSpriteMap: Map<Node, MultiThemeSprite> = new Map();

    private _displayType: TutorialType = null;
    private _playList: TutorialPage[] = null;
    private _pageIndex = 0;
    private _closeBySkip = true;

    @property({ type: Enum(TutorialPage) })
    public get displayPage() {
        return this._displayPage;
    }
    public set displayPage(value: TutorialPage) {
        this._displayPage = value;
        this.multiThemeSource.theme = value;
        if (!EDITOR) {
            this.pageIndicator.items.forEach((item, idx) => {
                let sprite = this._itemMultiThemeSpriteMap.get(item);
                if (!sprite) {
                    sprite = item.getComponent(MultiThemeSprite);
                    this._itemMultiThemeSpriteMap.set(item, sprite);
                }
                sprite.theme = idx === this._playList.indexOf(value) ? 1 : 0;
            });
        }
    }

    public get isDisplaying() {
        return this.page.isVisible;
    }

    public get displayType() {
        return this._displayType;
    }

    private get pageIndex() {
        return this._pageIndex;
    }
    private set pageIndex(value) {
        this._pageIndex = value;
        this.displayPage = this._playList[value];
        if (value === this._playList.length - 1) {
            this.nextButtonText.theme = 1;
            this.skipButton.node.active = false;
        } else {
            this.nextButtonText.theme = 0;
            this.skipButton.node.active = true;
        }
    }

    private get multiThemeSource() {
        if (!this._multiThemeSource) {
            this._multiThemeSource = this.getComponent(MultiThemeSource);
        }
        return this._multiThemeSource;
    }

    private get page() {
        if (!this._page) {
            this._page = this.getComponent(Page);
        }
        return this._page;
    }

    private get nextButtonText() {
        if (!this._nextButtonText) {
            this._nextButtonText = this.nextButton.getComponentInChildren(MultiThemeLabel);
        }
        return this._nextButtonText;
    }

    public static tryFirstDisplay(type: TutorialType): boolean {
        if (DISABLE_AUTOPLAY_TUTORIAL) {
            return false;
        }
        // disable multiple play tutorial for now since the tutorial need to adjust according to UI changes.
        if (type === TutorialType.MultiplePlay || type === TutorialType.SimpleMultiplePlay) {
            return false;
        }
        if (Lobby.setting[TUTORIAL_SETTING_MAP[type].show]) {
            return false;
        }
        Lobby.setting[TUTORIAL_SETTING_MAP[type].show] = true;
        Lobby.scene.getNode(NodeName.TutorialPlayer).getComponent(TutorialPlayer).display(type);
        return true;
    }

    public onLoad() {
        this.nextButton.on(Button.EventType.CLICK, this._nextPage, this);
        this.page.on(Page.EVENT_TYPE.CLOSE_END, () => {
            if (!this._closeBySkip) {
                return;
            }
            Lobby.activity.send(FeatureActivityEventName.SkipTutorial, { TutorialID: TUTORIAL_NAME_MAP[this._displayType] });
        });
        const index = TUTORIAL_PLAYLIST_MAP[TutorialType.MacauLobby].indexOf(TutorialPage.Lobby_BackToOuterLobby);
        // remove outer lobby tutorial page for non Win Slot UI since there is no outer lobby in non Win Slot UI
        TUTORIAL_PLAYLIST_MAP[TutorialType.MacauLobby].splice(index, 1);
        this._playList = TUTORIAL_PLAYLIST_MAP[TutorialType.MacauLobby];
    }

    public display(type: TutorialType) {
        this._displayType = type;
        this._playList = TUTORIAL_PLAYLIST_MAP[type];
        this.pageIndicator.displayItemCount = this._playList.length;
        this.pageIndex = 0;
        this.focusOnMask.active = true;
        this.infoBoard.active = true;
        this._closeBySkip = true;
        Lobby.setting[TUTORIAL_SETTING_MAP[type].show] = true;
        Lobby.activity.send(FeatureActivityEventName.OpenTutorial, { TutorialID: TUTORIAL_NAME_MAP[type] });
        this.page.open();
        this.emit(TutorialEventType.DISPLAY, type);
    }

    public waitForClose() {
        if (!this.page.isVisible) {
            return Promise.resolve();
        }
        return this.page.waitForClose();
    }

    public close(force = false, playTransition = true) {
        this._closeBySkip = false;
        this.page.close(force, playTransition);
    }

    private async _nextPage() {
        if (this.pageIndex === this._playList.length - 1) {
            this.focusOnMask.active = false;
            this.infoBoard.active = false;
            this.close();
        } else {
            this.pageIndex++;
            if (
                // Lobby.hall.table &&
                // !Lobby.table.hasJP &&
                this.displayPage === TutorialPage.Play_JackpotPercentageBar ||
                this.displayPage === TutorialPage.SimpleMultiplePlay_JackpotPercentageBar
            ) {
                this.pageIndex++;
            } else if (!Lobby.jackpot.isOpen && this.displayPage === TutorialPage.Lobby_LobbyJackpot) {
                this.pageIndex++;
            }
        }
    }
}
